import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as aggregates from "common/aggregates";
import * as arrays from "common/arrays";
import * as filters from "common/filters";
import * as formulae from "common/formulae";
import Metrics from "components/common/Metrics";
import * as staticDataService from "services/staticDataService";

const context = "review metrics";

const hasKeyInData = (data, key) =>
  Array.isArray(data) &&
  data.length > 0 &&
  data.some(
    (row) =>
      row != null &&
      Object.prototype.hasOwnProperty.call(row, key) &&
      row[key] != null
  );

const collectFormulaKeys = (node, keys = new Set()) => {
  if (!node || typeof node !== "object") return keys;
  if (node.key) keys.add(node.key);
  collectFormulaKeys(node.a, keys);
  collectFormulaKeys(node.b, keys);
  return keys;
};

const metricConfigHasRequiredKeys = (config, data) => {
  if (config.formula != null) {
    const keys = collectFormulaKeys(config.formula);
    return [...keys].every((key) => hasKeyInData(data, key));
  }
  if (config.key != null) {
    return hasKeyInData(data, config.key);
  }
  return false;
};

const extractBaseMetrics = (submissions) => {
  const metrics = {
    submissionCount: aggregates.aggregatedByKey(
      aggregates.METHOD.COUNT_UNIQUE,
      "submissionId",
      submissions,
      context
    ),
    layerCount: aggregates.aggregatedByKey(
      aggregates.METHOD.COUNT,
      "submissionId",
      submissions,
      context
    ),
    layerStats: {},
  };

  if (!arrays.hasItems(submissions)) {
    return metrics;
  }

  submissions.forEach((submission) => {
    if (metrics.layerStats[submission.status] == null) {
      metrics.layerStats[submission.status] = 0;
    }
    metrics.layerStats[submission.status]++;
  });

  return metrics;
};

const extractCustomMetrics = (submissions, config) => {
  if (!arrays.hasItems(config)) {
    return [];
  }

  return config
    .filter((x) => metricConfigHasRequiredKeys(x, submissions))
    .map((x) => {
      const filteredSubmissions = filters.apply(
        x.filters,
        submissions,
        context
      );

      const value =
        x.formula != null
          ? formulae.apply(x.formula, filteredSubmissions, context)
          : aggregates.aggregatedByKey(
              x.method,
              x.key,
              filteredSubmissions,
              context
            );

      return {
        ...x,
        value: value,
      };
    });
};

const statusMeta = (status, labelsConfig, theme) => {
  const colours = {
    CLOSED: theme.brand.status.closed,
    OPPORTUNITY: theme.brand.status.opportunity,
    IN_PROGRESS: theme.brand.status.inProgress,
    NOT_TAKEN_UP: theme.brand.status.notTakenUp,
    BOUND: theme.brand.status.bound,
    UNKNOWN: theme.brand.status.closed,
  };

  const config = labelsConfig.filter((x) => x.statusKey === status)[0];

  return {
    label: config?.displayName ?? "Unknown",
    colour: colours[config?.icon] ?? colours["UNKNOWN"],
  };
};

const chartData = (layerStats, labelsConfig, theme) => {
  if (layerStats == null || labelsConfig == null) {
    return null;
  }

  return Object.entries(layerStats).map(([k, v]) => {
    return { value: v, ...statusMeta(k, labelsConfig, theme) };
  });
};

const metricsToDisplay = (baseMetrics, customMetrics, labelsConfig, theme) => [
  {
    label: "Submissions",
    value: baseMetrics.submissionCount,
  },
  {
    label: "Layers",
    value: baseMetrics.layerCount,
  },
  ...customMetrics,
  {
    label: "Status",
    data: chartData(baseMetrics.layerStats, labelsConfig, theme),
  },
];

const DashboardReportSummary = ({ submissions, config }) => {
  const theme = useTheme();
  const { data: labelsConfig } = staticDataService.useConfigQuery(
    "layer_status_labels_v2"
  );

  if (config == null) {
    return <></>;
  }

  if (arrays.isArray(config)) {
    config = { columns: config };
  }

  const toDisplay = config?.excludeBaseMetrics
    ? extractCustomMetrics(submissions, config.columns)
    : metricsToDisplay(
        extractBaseMetrics(submissions),
        extractCustomMetrics(submissions, config.columns),
        labelsConfig?.labels,
        theme
      );

  return (
    <Box
      sx={{
        margin: "0.5rem 0",
        padding: "0.875rem 0.375rem",
        background: (theme) => theme.brand.gray[500],
        borderRadius: "0.625rem",
      }}
    >
      <Metrics metrics={toDisplay} />
    </Box>
  );
};

export default DashboardReportSummary;
